import { useMemo } from 'react';
import { QueryResult, useQuery } from 'react-query';
import { RefDataEmployerCategoryApi, EmployerCategory, VerificationType } from 'eve-account-client';
import { useConfiguration } from './BaseApi';

const useRefDataEmployerCategoryApi = (): RefDataEmployerCategoryApi => {
  const configuration = useConfiguration();
  const api = useMemo(() => new RefDataEmployerCategoryApi(configuration), [configuration]);
  return api;
};

const getEmployerCategories = async (api: RefDataEmployerCategoryApi): Promise<EmployerCategory[]> => {
  const request = api.getEmployerCategories({});

  // TODO: Clean this up (use immutable array methods like `concat()`?)
  return (await request).reduce((categoryList, category) => {
    if (category.groupName) {
      // Add the category, but set its parent value to the group name.
      categoryList.push({
        ...category,
        parentValue: category.groupName,
      });

      const existingGroupIndex = categoryList.findIndex(({ value }) => value === category.groupName);

      if (existingGroupIndex >= 0) {
        // If there's an existing group, update the display order of the group
        // to be the smaller of the current group's display order and that of
        // the latest category in the group.

        // eslint-disable-next-line no-param-reassign
        categoryList[existingGroupIndex].displayOrder = Math.min(
          categoryList[existingGroupIndex].displayOrder,
          category.displayOrder,
        );
      } else {
        // If there isn't an existing group, add a new entry for the group.
        categoryList.push({
          ...category,
          value: category.groupName,
          displayName: category.groupName,
          groupName: undefined,
        });
      }
    } else {
      // Skip processing the category and just add it.
      categoryList.push(category);
    }

    return categoryList;
  }, [] as EmployerCategory[]);
};

export const useEmployerCategoriesQuery = (): QueryResult<EmployerCategory[], Error | Response> => {
  const api = useRefDataEmployerCategoryApi();

  return useQuery<EmployerCategory[], Error | Response>(['employerCategories'], () => getEmployerCategories(api));
};

const getVerificationTypesByCategoryCode = (
  categoryCode: string,
  api: RefDataEmployerCategoryApi,
): Promise<VerificationType[]> => {
  const request = api.getVerificationTypesByCategoryCode({ categoryCode });
  return request;
};

export const useVerificationTypesQuery = (categoryCode?: string): QueryResult<VerificationType[], Error | Response> => {
  const api = useRefDataEmployerCategoryApi();

  return useQuery<VerificationType[], Error | Response>(
    ['verificationTypes', categoryCode],
    () => getVerificationTypesByCategoryCode(categoryCode as string, api),
    {
      enabled: Boolean(categoryCode),
    },
  );
};
